import { Grid } from "@mui/material";
import { FormikProps } from "formik";
import React from "react";
import Flex from "../../../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../../../components/Text";
import { Form2Payload } from "../../../../../../../../scheduling/appointment/encounter/encounter-constant";
import {
  optionLabel,
  MSE_OVERALL_NOTE_KEY,
  PROGRESS_MSE_FIELDS,
  PROGRESS_RISK_ACT_FIELDS,
  PROGRESS_RISK_ACT_OPTIONS,
  PROGRESS_RISK_LEVEL_OPTIONS,
} from "./ProgressNoteConstant";

interface SummaryProps {
  formik: FormikProps<Form2Payload>;
}

// One "Label: value" line, hidden when there is nothing to print.
const SummaryLine = ({ label, value }: { label: string; value?: string }) =>
  value ? (
    <Text fontSize="14px">
      <Text component="span" fontSize="14px" fontWeight="bold">
        {`${label}: `}
      </Text>
      {value}
    </Text>
  ) : null;

/**
 * Read-only summary of the Mental Status Examination — the components the
 * provider filled in, two per row, plus the overall note.
 */
export const MentalStatusSummary: React.FC<SummaryProps> = ({ formik }) => {
  const values = formik.values;

  const filled = PROGRESS_MSE_FIELDS.map((field) => ({
    key: field.key,
    label: field.label,
    value: optionLabel(field.options, values?.[field.key] as string),
  })).filter((field) => !!field.value);

  const overallNote = (values?.[MSE_OVERALL_NOTE_KEY] as string) || "";

  if (!filled.length && !overallNote) return null;

  return (
    <Flex flexDirection="column" gap={0.5} width="100%" mt={0.5}>
      <Grid container spacing={0.2}>
        {filled.map((field) => (
          <Grid
            key={field.key}
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <SummaryLine label={field.label} value={field.value} />
          </Grid>
        ))}
      </Grid>
      <SummaryLine label="Overall MSE Note" value={overallNote} />
    </Flex>
  );
};

/**
 * Read-only summary of the Risk Assessment — the denial flag, level, the three
 * act answers and the free-text blocks.
 */
export const ProgressRiskAssessmentSummary: React.FC<SummaryProps> = ({
  formik,
}) => {
  const values = formik.values;

  const actAnswers = PROGRESS_RISK_ACT_FIELDS.map((field) => ({
    ...field,
    value: optionLabel(
      PROGRESS_RISK_ACT_OPTIONS,
      values?.[field.key] as string
    ),
  })).filter((field) => !!field.value);

  const level = optionLabel(
    PROGRESS_RISK_LEVEL_OPTIONS,
    values?.riskLevelOfRisk
  );
  const deniesAll = !!values?.riskDeniesAllAreas;
  const riskFactors = values?.riskFactors || "";
  const protectiveFactors = values?.riskProtectiveFactors || "";
  const additionalDetails = values?.riskAdditionalDetails || "";

  const hasContent =
    deniesAll ||
    !!level ||
    actAnswers.length > 0 ||
    !!riskFactors ||
    !!protectiveFactors ||
    !!additionalDetails;

  if (!hasContent) return null;

  return (
    <Flex flexDirection="column" gap={0.25} width="100%" mt={0.5}>
      {deniesAll && (
        <Text fontSize="14px">• Patient denies all areas of risk</Text>
      )}
      <SummaryLine label="Level of Risk" value={level} />
      {actAnswers.map((field) => (
        <SummaryLine key={field.key} label={field.label} value={field.value} />
      ))}
      <SummaryLine label="Risk Factors" value={riskFactors} />
      <SummaryLine label="Protective Factors" value={protectiveFactors} />
      <SummaryLine label="Additional Details" value={additionalDetails} />
    </Flex>
  );
};

/* Read-only summary of the Recommendation and the prescribed frequency.
   Dropped: the Recommendation block is edited inline on the note now
   (<ProgressNoteRecommendation/>), so there is no modal left to summarise.
export const RecommendationSummary: React.FC<SummaryProps> = ({ formik }) => {
  const values = formik.values;

  const recommendation = optionLabel(
    PROGRESS_RECOMMENDATION_OPTIONS,
    values?.recommendation
  );
  const frequency = optionLabel(
    PROGRESS_TREATMENT_FREQUENCY_OPTIONS,
    values?.prescribedTreatmentFrequency
  );

  if (!recommendation && !frequency) return null;

  return (
    <Flex flexDirection="column" gap={0.25} width="100%" mt={0.5}>
      <SummaryLine label="Recommendation" value={recommendation} />
      <SummaryLine
        label="Prescribed Frequency of Treatment"
        value={frequency}
      />
    </Flex>
  );
}; */
